/**
 * Comparador de ofertas — Iter277 (feedback delegados: comparar 2-3
 * licitaciones similares lado a lado para decidir en cuál presentarse).
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Trophy, Clock, Layers, Euro, TrendingUp } from "lucide-react";
import { API } from "@/context/AuthContext";
import { KpiBox } from "@/components/exec_summary/KpiBox";

const fmtEur = (n) =>
  n == null ? "—" :
  new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n);

export default function ComparadorOfertas({ externalIds }) {
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.post(`${API}/delegado/oportunidades/comparar`, { external_ids: externalIds })
      .then((r) => setItems(r.data.items))
      .catch(() => toast.error("No se pudo cargar el comparador"))
      .finally(() => setLoading(false));
  }, [externalIds]);

  if (loading) return <div className="text-center py-16 text-slate-400" data-testid="delegado-comparador-loading"><Loader2 className="animate-spin mx-auto mb-2" size={24} />Cargando comparador…</div>;
  if (!items) return null;

  const valid = items.filter((it) => !it.error);
  // Resalta el mejor valor numérico entre columnas (score más alto, menor duración, etc. no se infiere; sólo se marca el score IA más alto como referencia).
  const bestScore = Math.max(...valid.map((it) => it.disponible_sin_ia?.ai_score || 0));

  return (
    <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${items.length}, minmax(0, 1fr))` }} data-testid="delegado-comparador-grid">
      {items.map((it, i) => {
        if (it.error) {
          return <div key={i} className="bg-rose-50 border border-rose-200 rounded-xl p-4 text-xs text-rose-600">No se pudo cargar esta oportunidad.</div>;
        }
        const info = it.disponible_sin_ia || {};
        const kpi = it.kpi_extraction;
        const isBest = info.ai_score === bestScore && bestScore > 0;
        return (
          <div key={i} className={`bg-white border rounded-xl p-4 space-y-3 ${isBest ? "border-emerald-400 ring-1 ring-emerald-200" : "border-slate-200"}`} data-testid={`delegado-comparador-col-${i}`}>
            {isBest && <span className="text-[10px] bg-emerald-100 text-emerald-700 font-bold px-2 py-0.5 rounded-full flex items-center gap-1 w-fit"><Trophy size={10} /> Mayor score IA</span>}
            <div>
              <p className="text-sm font-bold text-slate-800 line-clamp-2">{info.title}</p>
              <p className="text-xs text-slate-500">{info.organism} · {info.municipality}</p>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <KpiBox label="Importe" value={fmtEur(info.value_eur)} />
              <KpiBox label="Score IA" value={info.ai_score ?? "—"} />
              <KpiBox label="Duración" icon={Clock} value={kpi?.duracion_anios ? `${kpi.duracion_anios} años` : "—"} />
              <KpiBox label="Lotes" icon={Layers} value={kpi?.lotes_count ?? "—"} />
              <KpiBox label="CAPEX pliego" icon={Euro} value={kpi?.capex_pliego_eur ? fmtEur(kpi.capex_pliego_eur) : "—"} />
              <KpiBox label="OPEX/año" icon={TrendingUp} value={kpi?.opex_pliego_eur_anual ? fmtEur(kpi.opex_pliego_eur_anual) : "—"} />
            </div>
            {!kpi && <p className="text-[11px] text-amber-600 bg-amber-50 rounded-lg p-2">Sin analizar — abre su Resumen Ejecutivo y pulsa "Analizar pliego".</p>}
            {it.adjudicador_actual && <p className="text-[11px] text-slate-500"><b>Adjudicatario actual:</b> {it.adjudicador_actual}</p>}
            {it.nearby_market && <p className="text-[11px] text-slate-500"><b>Mercado en {it.nearby_market.provincia}:</b> {it.nearby_market.n_licitaciones} licitaciones</p>}
          </div>
        );
      })}
    </div>
  );
}
